import { ResumableUploadError } from "./ResumableUploadError";

const UPLOAD_LEASE_STORAGE_PREFIX = "multiburo:upload-lease";
const UPLOAD_LEASE_DURATION_MS = 30_000;
const UPLOAD_LEASE_HEARTBEAT_MS = 10_000;

type UploadLease = {
  readonly ownerId: string;
  readonly expiresAt: number;
};

type UploadLeaseStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type ActiveLease = {
  count: number;
  readonly ownerId: string;
  readonly storage: UploadLeaseStorage;
  heartbeat: ReturnType<typeof setInterval>;
};

type BrowserLockManager = {
  request<T>(
    name: string,
    options: { readonly mode: "exclusive"; readonly ifAvailable: true },
    callback: (lock: unknown | null) => Promise<T>,
  ): Promise<T>;
};

const activeLeases = new Map<string, ActiveLease>();
const memoryLeaseValues = new Map<string, string>();
const memoryLeaseStorage: UploadLeaseStorage = {
  getItem: (key) => memoryLeaseValues.get(key) ?? null,
  setItem: (key, value) => {
    memoryLeaseValues.set(key, value);
  },
  removeItem: (key) => {
    memoryLeaseValues.delete(key);
  },
};

function defaultLeaseStorage(): UploadLeaseStorage | null {
  try {
    return typeof globalThis.localStorage === "undefined"
      ? null
      : globalThis.localStorage;
  } catch {
    return null;
  }
}

function uploadLeaseKey(requirementId: string, recoveryScope: string): string {
  return `${UPLOAD_LEASE_STORAGE_PREFIX}:${recoveryScope}:${requirementId}`;
}

function readLease(storage: Pick<Storage, "getItem">, key: string) {
  try {
    const serialized = storage.getItem(key);
    if (!serialized) return null;
    const lease = JSON.parse(serialized) as UploadLease;
    if (!lease.ownerId || !Number.isFinite(lease.expiresAt)) return null;
    return lease;
  } catch {
    return null;
  }
}

function writeLease(
  storage: Pick<Storage, "setItem">,
  key: string,
  ownerId: string,
): void {
  storage.setItem(
    key,
    JSON.stringify({
      ownerId,
      expiresAt: Date.now() + UPLOAD_LEASE_DURATION_MS,
    } satisfies UploadLease),
  );
}

function acquireLease(
  requirementId: string,
  recoveryScope: string,
  ownerId: string,
  storage: UploadLeaseStorage,
  authoritativeBrowserLock = false,
): () => void {
  const key = uploadLeaseKey(requirementId, recoveryScope);
  const active = activeLeases.get(key);
  if (active?.ownerId === ownerId) {
    active.count += 1;
    return () => releaseLease(key, ownerId);
  }

  const existing = readLease(storage, key);
  if (
    !authoritativeBrowserLock &&
    existing &&
    existing.ownerId !== ownerId &&
    existing.expiresAt > Date.now()
  ) {
    throw new ResumableUploadError(
      "UPLOAD_ACTIVE_IN_ANOTHER_TAB",
      "Ya hay una carga en curso para este documento en otra pestaña.",
    );
  }

  try {
    writeLease(storage, key, ownerId);
    const acquired = readLease(storage, key);
    if (acquired?.ownerId !== ownerId) {
      throw new ResumableUploadError(
        "UPLOAD_ACTIVE_IN_ANOTHER_TAB",
        "Ya hay una carga en curso para este documento en otra pestaña.",
      );
    }
  } catch (error: unknown) {
    if (error instanceof ResumableUploadError) throw error;
    // Uploads still work when browser storage is unavailable. The server
    // remains the final concurrency authority in this fallback path.
    return () => undefined;
  }

  const heartbeat = setInterval(() => {
    const current = readLease(storage, key);
    if (current?.ownerId !== ownerId) return;
    try {
      writeLease(storage, key, ownerId);
    } catch {
      // A transient storage failure must not abort an in-flight upload.
    }
  }, UPLOAD_LEASE_HEARTBEAT_MS);
  activeLeases.set(key, { count: 1, ownerId, storage, heartbeat });

  return () => releaseLease(key, ownerId);
}

function browserLockManager(): BrowserLockManager | null {
  if (typeof globalThis.navigator === "undefined") return null;
  const navigatorWithLocks = globalThis.navigator as Navigator & {
    readonly locks?: BrowserLockManager;
  };
  return navigatorWithLocks.locks ?? null;
}

function concurrentUploadError(): ResumableUploadError {
  return new ResumableUploadError(
    "UPLOAD_ACTIVE_IN_ANOTHER_TAB",
    "Ya hay una carga en curso para este documento en otra pestaña.",
  );
}

function releaseLease(key: string, ownerId: string): void {
  const active = activeLeases.get(key);
  if (!active || active.ownerId !== ownerId) return;
  active.count -= 1;
  if (active.count > 0) return;

  clearInterval(active.heartbeat);
  activeLeases.delete(key);
  try {
    if (readLease(active.storage, key)?.ownerId === ownerId) {
      active.storage.removeItem(key);
    }
  } catch {
    // The short expiry makes an unreleased browser lease self-healing.
  }
}

export async function withVaultUploadRecoveryLease<T>({
  requirementId,
  recoveryScope,
  ownerId,
  operation,
  storage = defaultLeaseStorage(),
}: {
  readonly requirementId: string;
  readonly recoveryScope: string;
  readonly ownerId: string;
  readonly operation: () => Promise<T>;
  readonly storage?: UploadLeaseStorage | null;
}): Promise<T> {
  // Web Locks remain authoritative even if persistent storage is disabled.
  // The in-memory adapter supplies same-tab reentrancy in that case.
  const effectiveStorage = storage ?? memoryLeaseStorage;
  const key = uploadLeaseKey(requirementId, recoveryScope);

  // High-level form flows wrap the lower-level upload mutation. Re-enter the
  // lease locally so the nested call does not deadlock on its own Web Lock.
  if (activeLeases.get(key)?.ownerId === ownerId) {
    const release = acquireLease(
      requirementId,
      recoveryScope,
      ownerId,
      effectiveStorage,
    );
    try {
      return await operation();
    } finally {
      release();
    }
  }

  const locks = browserLockManager();
  if (locks) {
    return locks.request(
      key,
      { mode: "exclusive", ifAvailable: true },
      async (lock) => {
        if (!lock) throw concurrentUploadError();
        // Acquiring the browser lock proves that a previous tab is gone, so
        // it is safe to replace a not-yet-expired localStorage fallback lease.
        const release = acquireLease(
          requirementId,
          recoveryScope,
          ownerId,
          effectiveStorage,
          true,
        );
        try {
          return await operation();
        } finally {
          release();
        }
      },
    );
  }

  const release = acquireLease(
    requirementId,
    recoveryScope,
    ownerId,
    effectiveStorage,
  );
  try {
    return await operation();
  } finally {
    release();
  }
}
